import React, { useCallback, useEffect, useState } from 'react';
import { Box, Stack, Typography, Chip, TextField, IconButton, Tooltip, Avatar, Collapse, InputBase } from '@mui/material';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import http, { apiErrorMessage } from '../../Api/http';
import { GetAuditLog } from '../../Api/Api';
import { Panel, EmptyState, SkeletonRows, card, field } from '../uiKit';
import { avatarTone, initialsFromName as initials } from '../../utils/format';

const todayISO = () => new Date().toISOString().slice(0, 10);
const toDMY = (iso) => {
    if (!iso) return '';
    const [y, m, d] = String(iso).split('-');
    return d ? `${d}-${m}-${y}` : '';
};

// Read-only "who did what today" feed. The server already scopes entries to
// whichever of Vacancies/Candidates/Interviews the caller holds — a role
// with just one of those modules still gets a 200 with only its own slice,
// so this tab never assumes actionCounts/entries reflect everything.
// The server labels are plural ("Vacancies added"); a count of 1 should read "Vacancy added".
const singular = (label) => String(label)
    .replace(/\bVacancies\b/g, 'Vacancy')
    .replace(/\b(Candidates|Interviews|Communications|Rounds)\b/g, (w) => w.slice(0, -1))
    .replace(/\b(changes|actions)\b/g, (w) => w.slice(0, -1));
const countLabel = (c) => `${c.count} ${Number(c.count) === 1 ? singular(c.label) : c.label}`;

export default function ActivityTab() {
    const [date, setDate] = useState(todayISO());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');
    const [openId, setOpenId] = useState(null);
    const [userQ, setUserQ] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetAuditLog, { params: { date: toDMY(date) } });
            if (body?.error) throw new Error(body.message || 'Could not load the activity log.');
            setData(body?.data || null);
            setLoadError('');
        } catch (err) {
            setData(null);
            setLoadError(apiErrorMessage(err, 'Could not load the activity log.'));
        } finally {
            setLoading(false);
        }
    }, [date]);

    useEffect(() => { load(); }, [load]);

    const counts = Array.isArray(data?.actionCounts) ? data.actionCounts : [];
    const entries = Array.isArray(data?.entries) ? data.entries : [];
    const needle = userQ.trim().toLowerCase();
    const shownEntries = needle
        ? entries.filter((e) => [e.actorName, e.actor].some((x) => String(x || '').toLowerCase().includes(needle)))
        : entries;

    return (
        <Box sx={{ p: 2 }}>
            <Stack direction="row" spacing={1.2} sx={{ ...card, borderRadius: '12px', p: 1.5, mb: 2, alignItems: 'center', flexWrap: 'wrap', rowGap: 1 }}>
                <TextField
                    label="Date" type="date" size="small" value={date} onChange={(e) => setDate(e.target.value)}
                    sx={{ ...field, width: 170 }}
                    slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: todayISO() } }}
                />
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center', bgcolor: '#F8FAFC', border: '1px solid #E5E7EB', borderRadius: '7px', px: 1.4, height: 40, width: { xs: '100%', sm: 240 } }}>
                    <SearchRoundedIcon sx={{ fontSize: 18, color: '#98A0AE' }} />
                    <InputBase placeholder="Search user…" value={userQ} onChange={(e) => setUserQ(e.target.value)} sx={{ fontSize: 13, flex: 1 }} />
                </Stack>
                <Tooltip arrow title="Reload">
                    <IconButton onClick={load} disabled={loading} sx={{ border: '1px solid #E5E7EB', borderRadius: '7px', color: '#64748B', height: 40, width: 40, '&:hover': { bgcolor: '#F1EEFE', color: '#7C5CFC' } }}>
                        <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            </Stack>

            <Panel
                title="Activity"
                icon={HistoryRoundedIcon}
                sx={{ borderRadius: '12px' }}
                chip={data ? `${data.totalActions ?? entries.length} action${(data.totalActions ?? entries.length) === 1 ? '' : 's'}` : undefined}
            >
                {loading ? (
                    <SkeletonRows count={5} />
                ) : loadError ? (
                    <EmptyState icon={HistoryRoundedIcon} title="Couldn't load the activity log" hint={loadError} />
                ) : entries.length === 0 ? (
                    <EmptyState icon={HistoryRoundedIcon} title="Nothing happened on this day" hint="Vacancy, candidate and interview actions you're allowed to see will show up here." />
                ) : (
                    <Box sx={{ p: 1.5 }}>
                        {counts.length > 0 && (
                            <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                {counts.map((c) => (
                                    <Chip key={c.action} label={countLabel(c)} size="small" sx={{ height: 26, fontSize: 11.5, fontWeight: 700, bgcolor: '#F1EEFE', color: '#7C5CFC', '& .MuiChip-label': { px: 1.4 } }} />
                                ))}
                            </Stack>
                        )}
                        {shownEntries.length === 0 && (
                            <EmptyState icon={HistoryRoundedIcon} title="No activity from that user" hint="Try a different name, or clear the search." />
                        )}
                        <Stack spacing={0.8}>
                            {shownEntries.map((e) => {
                                const open = openId === e.id;
                                const hasDetails = e.details && Object.keys(e.details).length > 0;
                                return (
                                    <Box key={e.id} sx={{ border: '1px solid #F1F0F9', borderRadius: '9px', overflow: 'hidden' }}>
                                        <Stack
                                            direction="row" spacing={1.3}
                                            onClick={() => hasDetails && setOpenId(open ? null : e.id)}
                                            sx={{ alignItems: 'center', p: 1.3, cursor: hasDetails ? 'pointer' : 'default', '&:hover': hasDetails ? { bgcolor: '#FAFAFD' } : undefined }}
                                        >
                                            <Avatar sx={{ width: 32, height: 32, ...avatarTone(e.actorName || e.actor || ''), fontSize: 11.5, fontWeight: 700, flexShrink: 0 }}>
                                                {initials(e.actorName || e.actor || '?')}
                                            </Avatar>
                                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                                <Typography sx={{ fontSize: 13, color: '#0F172A' }}>
                                                    <b>{e.actorName || e.actor}</b> {e.summary}
                                                </Typography>
                                                <Typography sx={{ fontSize: 11, color: '#98A0AE' }}>{e.time}</Typography>
                                            </Box>
                                            {hasDetails && <ExpandMoreRoundedIcon sx={{ fontSize: 18, color: '#98A0AE', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .15s', flexShrink: 0 }} />}
                                        </Stack>
                                        {hasDetails && (
                                            <Collapse in={open}>
                                                <Box sx={{ px: 1.6, py: 1.2, borderTop: '1px solid #F1F0F9', bgcolor: '#FAFAFD' }}>
                                                    {Object.entries(e.details).map(([k, v]) => (
                                                        <Stack key={k} direction="row" sx={{ justifyContent: 'space-between', py: 0.4, gap: 1.5 }}>
                                                            <Typography sx={{ fontSize: 11, color: '#98A0AE', textTransform: 'capitalize', flexShrink: 0 }}>{k.replace(/([A-Z])/g, ' $1').trim()}</Typography>
                                                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#334155', textAlign: 'right' }}>{String(v)}</Typography>
                                                        </Stack>
                                                    ))}
                                                </Box>
                                            </Collapse>
                                        )}
                                    </Box>
                                );
                            })}
                        </Stack>
                    </Box>
                )}
            </Panel>
        </Box>
    );
}
